import Head from "next/head";
import React from "react";
import { SEOProps } from "@/types/seo";
import {
  sanitizeMetaContent,
  formatCanonicalUrl,
  generateStructuredData,
  truncateDescription,
  generateRobotsContent,
} from "@/utils/seo";

const DEFAULT_TITLE =
  "Find Verified Properties in Nigeria | Buy, Rent, Shortlets & Lease | FNP";
const DEFAULT_DESCRIPTION =
  "Discover verified properties for sale, rent, lease and shortlets across Nigeria. Connect with trusted realtors and developers, request property inspections, and invest with confidence on FindNigeriaProperties.";
const DEFAULT_IMAGE = "/images/og-default.jpg";
const DEFAULT_SITE_NAME = "FNP";

export const SEOHead: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  keywords,
  robots,
  ogData,
  twitterData,
  structuredData,
  noindex = false,
  nofollow = false,
  author,
  publishedTime,
  modifiedTime,
  section,
  tags,
  image,
  locale = "en_US",
  fbAppId,
}) => {
  const pageTitle = title ? `${title} | ${DEFAULT_SITE_NAME}` : DEFAULT_TITLE;
  const pageDescription = truncateDescription(
    description || DEFAULT_DESCRIPTION,
  );
  const pageImage = image || ogData?.image || DEFAULT_IMAGE;
  const robotsContent = generateRobotsContent(noindex, nofollow, robots);

  const openGraphTitle = ogData?.title || title || DEFAULT_TITLE;
  const openGraphDescription = ogData?.description || pageDescription;
  const openGraphImage = ogData?.image || pageImage;
  const openGraphUrl = ogData?.url || canonical;

  const fbAppIdValue =
    fbAppId || process.env.NEXT_PUBLIC_FACEBOOK_APP_ID || undefined;

  const secureImage =
    openGraphImage && typeof openGraphImage === "string"
      ? openGraphImage.startsWith("http://")
        ? openGraphImage.replace("http://", "https://")
        : openGraphImage
      : openGraphImage;

  const openGraphVideo = ogData?.videoUrl;
  const secureVideo =
    openGraphVideo && typeof openGraphVideo === "string"
      ? openGraphVideo.startsWith("http://")
        ? openGraphVideo.replace("http://", "https://")
        : openGraphVideo
      : openGraphVideo;

  const twitterTitle = twitterData?.title || openGraphTitle;
  const twitterDescription = twitterData?.description || openGraphDescription;
  const twitterImage = twitterData?.image || openGraphImage;

  return (
    <Head>
      <title>{pageTitle}</title>
      <meta name="description" content={sanitizeMetaContent(pageDescription)} />
      {keywords && (
        <meta name="keywords" content={sanitizeMetaContent(keywords)} />
      )}
      <meta name="robots" content={robotsContent} />
      {author && <meta name="author" content={sanitizeMetaContent(author)} />}

      {canonical && (
        <link rel="canonical" href={formatCanonicalUrl(canonical)} />
      )}

      <meta property="og:title" content={sanitizeMetaContent(openGraphTitle)} />
      <meta
        property="og:description"
        content={sanitizeMetaContent(openGraphDescription)}
      />
      <meta property="og:type" content={ogData?.type || "website"} />
      {openGraphUrl && (
        <meta property="og:url" content={formatCanonicalUrl(openGraphUrl)} />
      )}
      <meta property="og:image" content={openGraphImage} />
      {typeof secureImage === "string" && (
        <meta property="og:image:secure_url" content={secureImage} />
      )}
      {ogData?.imageAlt && (
        <meta
          property="og:image:alt"
          content={sanitizeMetaContent(ogData.imageAlt)}
        />
      )}
      {ogData?.imageWidth && (
        <meta property="og:image:width" content={String(ogData.imageWidth)} />
      )}
      {ogData?.imageHeight && (
        <meta property="og:image:height" content={String(ogData.imageHeight)} />
      )}
      {openGraphVideo && <meta property="og:video" content={openGraphVideo} />}
      {typeof secureVideo === "string" && (
        <meta property="og:video:secure_url" content={secureVideo} />
      )}
      {ogData?.videoType && (
        <meta property="og:video:type" content={ogData.videoType} />
      )}
      {ogData?.videoWidth && (
        <meta property="og:video:width" content={String(ogData.videoWidth)} />
      )}
      {ogData?.videoHeight && (
        <meta property="og:video:height" content={String(ogData.videoHeight)} />
      )}
      <meta
        property="og:site_name"
        content={ogData?.siteName || DEFAULT_SITE_NAME}
      />
      <meta property="og:locale" content={locale} />
      {fbAppIdValue && <meta property="fb:app_id" content={fbAppIdValue} />}

      <meta
        name="twitter:card"
        content={twitterData?.card || "summary_large_image"}
      />
      {twitterData?.site && (
        <meta
          name="twitter:site"
          content={sanitizeMetaContent(twitterData.site)}
        />
      )}
      {twitterData?.creator && (
        <meta
          name="twitter:creator"
          content={sanitizeMetaContent(twitterData.creator)}
        />
      )}
      <meta name="twitter:title" content={sanitizeMetaContent(twitterTitle)} />
      <meta
        name="twitter:description"
        content={sanitizeMetaContent(twitterDescription)}
      />
      <meta name="twitter:image" content={twitterImage} />
      {twitterData?.imageAlt && (
        <meta
          name="twitter:image:alt"
          content={sanitizeMetaContent(twitterData.imageAlt)}
        />
      )}

      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {section && (
        <meta
          property="article:section"
          content={sanitizeMetaContent(section)}
        />
      )}
      {tags &&
        tags.map((tag, index) => (
          <meta
            key={index}
            property="article:tag"
            content={sanitizeMetaContent(tag)}
          />
        ))}

      {structuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: generateStructuredData(structuredData),
          }}
        />
      )}
    </Head>
  );
};
